import React, {
    useState,
    useEffect,
    forwardRef,
    useImperativeHandle,
} from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Paper,
    CircularProgress,
    Typography,
    IconButton,
    Popover,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "../../../utils/axiosInstance";
import { useSnackbar } from "notistack";
import { Settings } from "@mui/icons-material";
import { ROLE, usePermissions } from "../../../utils/permissions";
import DeleteModal from "../../UI/atoms/Popup/DeleteModal";

const UserTable = forwardRef(({ roles, modules, onEdit }: any, ref) => {
    const [users, setUsers] = useState<any[]>([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [loader, setLoader] = useState(false);
    const [skipKeyQueue, setSkipKeyQueue] = useState<any>([]);
    const [moduleList, setModules] = useState<any[]>([]);
    const [roleMap, setRoleMap] = useState<any>({});
    const { enqueueSnackbar } = useSnackbar();
    const [showNextIcon, setShowNextIcon] = useState(true);
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [selectedUser, setSelectedUser] = useState<any>(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const { hasRole } = usePermissions();

    useImperativeHandle(
        ref,
        () => ({
            refreshUserList,
        }),
        []
    );

    useEffect(() => {
        setModules(modules);
    }, [modules]);

    useEffect(() => {
        if (roles.length) {
            setRoleMap(
                roles.reduce((acc: any, curr: any) => {
                    acc[curr.roleName] = curr.roleLabel;
                    return acc;
                }, {})
            );
        }
    }, [roles]);

    useEffect(() => {
        getUsers();
    }, []);

    const refreshUserList = async () => {
        setSkipKeyQueue([]);
        setPage(0);
        getUsers();
    };

    const getUsers = async (skip: string = "", limit = rowsPerPage) => {
        setLoader(true);
        try {
            const response = await axios.get(
                `${process.env.BASE_URL}api/v1/cms/user?limit=${limit}&paginationToken=${skip}`
            );
            const data = response.data;
            if (data.success) {
                setUsers(data.data.users);
                setShowNextIcon(data.data.paginationToken !== null);
                pushKey(`${data.data.paginationToken}`);
            }
        } catch (error) {
            console.error("Error fetching users:", error);
            enqueueSnackbar("Failed to fetch users", { variant: "error" });
        }
        setLoader(false);
    };

    const handleChangePage = async (event: unknown, newPage: number) => {
        let skipKeyValue = "";
        let queue;
        if (newPage < page) {
            queue = popTwice();
            setSkipKeyQueue([...queue]);
            skipKeyValue = queue[queue.length - 1] || "";
        } else {
            skipKeyValue = skipKeyQueue[skipKeyQueue.length - 1];
        }
        setPage(newPage);
        await getUsers(skipKeyValue);
    };

    const handleChangeRowsPerPage = async (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const rowPerPage = parseInt(event.target.value, 10);
        setRowsPerPage(rowPerPage);
        setPage(0);
        setSkipKeyQueue([]);
        await getUsers("", rowPerPage);
    };

    const pushKey = (key: any) => {
        setSkipKeyQueue((prev: any) => [...prev, key]);
    };

    const popKey = (queue: any[] = skipKeyQueue) => {
        const skipValue = [...queue];
        skipValue.pop();
        setSkipKeyQueue(skipValue);
        return skipValue;
    };

    const popTwice = () => {
        const queue1 = popKey();
        const queue = popKey(queue1);
        return queue;
    };

    const hasPermission = (user: any, module: string) => {
        return (
            (user.viewPermissions &&
                user.viewPermissions.split(",").includes(module)) ||
            (user.editPermissions &&
                user.editPermissions.split(",").includes(module))
        );
    };

    const handleSettingsClick = (
        event: React.MouseEvent<HTMLButtonElement>,
        user: any
    ) => {
        setAnchorEl(event.currentTarget);
        setSelectedUser(user);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setSelectedUser(null);
    };

    const handleEdit = () => {
        // Implement edit functionality
        onEdit(selectedUser);
        handleClose();
    };

    const handleDelete = () => {
        // Implement delete functionality
        deleteUserAPI(selectedUser);
        handleClose();
    };

    const openDeleteModal = () => {
        setIsDeleteModalOpen(true);
    };

    const closeDeleteModal = () => {
        setIsDeleteModalOpen(false);
    };

    const deleteUserAPI = async (userInfo: any) => {
        try {
            const materInformation = await axios.delete(
                `${process.env.BASE_URL}api/v1/cms/user/${userInfo?.username}`
            );
            if (materInformation.data?.success) {
                enqueueSnackbar(materInformation.data?.message, {
                    variant: "success",
                });
                refreshUserList();
                closeDeleteModal();
            } else {
                enqueueSnackbar(materInformation.data?.message, {
                    variant: "error",
                });
            }
        } catch (error) {
            console.log("Error in getting master information", error);
        }
    };

    const open = Boolean(anchorEl);
    const id = open ? "simple-popover" : undefined;

    return (
        <div>
            {loader ? (
                <CircularProgress />
            ) : (
                <>
                    <TableContainer component={Paper}>
                        <Table aria-label="user table">
                            <TableHead>
                                <TableRow>
                                    <TableCell style={{ width: "20%" }}>
                                        <h3>Name</h3>
                                    </TableCell>
                                    <TableCell style={{ width: "10%" }}>
                                        <h3>Role</h3>
                                    </TableCell>
                                    <TableCell style={{ width: "8%" }}>
                                        <h3>Config</h3>
                                    </TableCell>
                                    <TableCell style={{ width: "8%" }}>
                                        <h3>Copy</h3>
                                    </TableCell>
                                    <TableCell style={{ width: "8%" }}>
                                        <h3>Ingestion</h3>
                                    </TableCell>
                                    <TableCell style={{ width: "8%" }}>
                                        <h3>Curation</h3>
                                    </TableCell>
                                    <TableCell style={{ width: "8%" }}>
                                        <h3>Metadata</h3>
                                    </TableCell>
                                    <TableCell style={{ width: "8%" }}>
                                        <h3>MAM</h3>
                                    </TableCell>
                                    <TableCell style={{ width: "8%" }}>
                                        <h3>Slate</h3>
                                    </TableCell>
                                    <TableCell style={{ width: "8%" }}>
                                        <h3>Dsar</h3>
                                    </TableCell>
                                    <TableCell style={{ width: "8%" }}>
                                        <h3>Genre</h3>
                                    </TableCell>
                                    {hasRole(ROLE.ADMIN) && (
                                        <TableCell style={{ width: "8%" }}>
                                            <Settings />
                                        </TableCell>
                                    )}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {users.map((user) => (
                                    <TableRow key={user.sub}>
                                        <TableCell style={{ width: "20%" }}>
                                            <Typography
                                                variant="subtitle2"
                                                component="div"
                                            >
                                                {user.firstName} {user.lastName}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                            >
                                                {user.email || "-"}
                                            </Typography>
                                        </TableCell>
                                        <TableCell style={{ width: "10%" }}>
                                            {(user.role &&
                                                roleMap[user.role]) ||
                                                "N/A"}
                                        </TableCell>
                                        <TableCell style={{ width: "8%" }}>
                                            {hasPermission(user, "config") && (
                                                <CheckIcon />
                                            )}
                                        </TableCell>
                                        <TableCell style={{ width: "8%" }}>
                                            {hasPermission(user, "locale") && (
                                                <CheckIcon />
                                            )}
                                        </TableCell>
                                        <TableCell style={{ width: "8%" }}>
                                            {hasPermission(
                                                user,
                                                "ingestion"
                                            ) && <CheckIcon />}
                                        </TableCell>
                                        <TableCell style={{ width: "8%" }}>
                                            {hasPermission(
                                                user,
                                                "curation"
                                            ) && <CheckIcon />}
                                        </TableCell>
                                        <TableCell style={{ width: "8%" }}>
                                            {hasPermission(
                                                user,
                                                "metadata"
                                            ) && <CheckIcon />}
                                        </TableCell>
                                        <TableCell style={{ width: "8%" }}>
                                            {hasPermission(user, "mam") && (
                                                <CheckIcon />
                                            )}
                                        </TableCell>
                                        <TableCell style={{ width: "8%" }}>
                                            {hasPermission(user, "slate") && (
                                                <CheckIcon />
                                            )}
                                        </TableCell>
                                        <TableCell style={{ width: "8%" }}>
                                            {hasPermission(user, "dsar") && (
                                                <CheckIcon />
                                            )}
                                        </TableCell>
                                        <TableCell style={{ width: "8%" }}>
                                            {hasPermission(user, "genre") && (
                                                <CheckIcon />
                                            )}
                                        </TableCell>

                                        {hasRole(ROLE.ADMIN) && (
                                            <TableCell style={{ width: "8%" }}>
                                                <IconButton
                                                    onClick={(event) =>
                                                        handleSettingsClick(
                                                            event,
                                                            user
                                                        )
                                                    }
                                                >
                                                    <MoreVertIcon />
                                                </IconButton>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[10, 25]}
                        component="div"
                        count={-1}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        slotProps={{
                            actions: {
                                nextButton: {
                                    disabled: !showNextIcon,
                                },
                            },
                        }}
                    />

                    <DeleteModal
                        title="Delete User?"
                        isOpen={isDeleteModalOpen}
                        handleClose={closeDeleteModal}
                        handleConfirm={handleDelete}
                    >
                        <p>
                            Are you sure you want to remove this user from the
                            account?
                        </p>
                    </DeleteModal>
                    <Popover
                        id={id}
                        open={open}
                        anchorEl={anchorEl}
                        onClose={handleClose}
                        anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "right",
                        }}
                        transformOrigin={{
                            vertical: "top",
                            horizontal: "right",
                        }}
                    >
                        <List>
                            <ListItem disablePadding>
                                <ListItemButton onClick={handleEdit}>
                                    {/* <ListItemIcon>
                                        <EditIcon />
                                    </ListItemIcon> */}
                                    <ListItemText primary="Edit" />
                                </ListItemButton>
                            </ListItem>
                            <ListItem disablePadding>
                                <ListItemButton onClick={openDeleteModal}>
                                    {/* <ListItemIcon>
                                        <DeleteIcon />
                                    </ListItemIcon> */}
                                    <ListItemText primary="Delete" />
                                </ListItemButton>
                            </ListItem>
                        </List>
                    </Popover>
                </>
            )}
        </div>
    );
});

export default UserTable;
